import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';

// Capture OAuth callback params at module load, before any component mounts.
// This survives AuthGuard redirects and React StrictMode double-invocation.
(() => {
  try {
    const params = new URLSearchParams(window.location.search);
    const pairs: [string, string][] = [
      ['ga_session', 'pending_ga_session'],
      ['ga_error', 'pending_ga_error'],
      ['google_ads_session', 'pending_google_ads_session'],
      ['google_ads_error', 'pending_google_ads_error'],
      ['meta_session', 'pending_meta_session'],
      ['meta_error', 'pending_meta_error'],
      // Advisory result of the on-demand Facebook Page grant (VIBE-1916); the
      // wizard resumes on the Pages step without a flash. `GET /pages` is the
      // authoritative answer, so losing this costs a flash, never correctness.
      ['meta_pages_granted', 'pending_meta_pages_granted'],
    ];
    for (const [param, key] of pairs) {
      const value = params.get(param);
      if (value) sessionStorage.setItem(key, value);
    }
  } catch { /* ignore */ }
})();
import { useAuthStore } from './stores/useAuthStore';
import { useConfigStore } from './stores/useConfigStore';
import { stashPostLoginRedirect } from './utils/postLoginRoute';
import Layout from './components/Layout';
import LeadSourceTracker from './components/LeadSourceTracker';
import CookieBanner from './components/CookieBanner';
import ApprovalModal from './components/ApprovalModal';
import LandingView from './features/landing/LandingView';
import ConnectView from './features/connect/ConnectView';
import ShopifyClaimView from './features/connect/ShopifyClaimView';
import ProcessingView from './features/processing/ProcessingView';
import SignView from './features/onboarding/SignView';
import CollectView from './features/onboarding/CollectView';
import ConfirmView from './features/onboarding/ConfirmView';
import ReportView from './features/report/ReportView';
import LeadSignupPage from './features/lead-portal/LeadSignupPage';
import AuthCallbackPage from './features/lead-portal/AuthCallbackPage';
import ResetPasswordPage from './features/auth/ResetPasswordPage';
import PrivacyPage from './features/legal/PrivacyPage';
import TermsPage from './features/legal/TermsPage';

function AuthGuard({ children }: { children: React.ReactNode }) {
  const { token, loading, restoreSession } = useAuthStore();
  const location = useLocation();

  useEffect(() => {
    restoreSession();
  }, [restoreSession]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-sm text-muted-foreground">
        Loading...
      </div>
    );
  }

  // Not authenticated → redirect to login. Preserve the Shopify claim target so a
  // Shopify-initiated install by a NOT-logged-in merchant is completed after login
  // (the `?shop=` param can't be reconstructed by the workflow router). Scoped to
  // the claim page — every other route is reconstructed by resolvePostLoginRoute.
  if (!token) {
    if (location.pathname === '/shopify/claim') {
      stashPostLoginRedirect(location.pathname + location.search);
    }
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Legacy route: the audit now runs as ONE unified pipeline on the single-page
// ProcessingView (analysis → refine on one Centrifugo run). Bookmarks, backend
// redirects and resume links to /generate-report/:chatId are forwarded to
// /processing/:chatId — there is no separate generating phase anymore.
function GenerateReportRedirect() {
  const { chatId } = useParams<{ chatId: string }>();
  return <Navigate to={`/processing/${chatId}`} replace />;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  // Runtime config comes from the backend (GET /api/config), not from
  // import.meta.env: this bundle is baked into the image, so a VITE_ value
  // could only change with a rebuild. Fetched once per page load (the store
  // memoizes it); until it answers — and if it never does — every flag reads
  // as off (VIBE-1786).
  useEffect(() => {
    useConfigStore.getState().loadRuntimeConfig();
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <LeadSourceTracker />
      <CookieBanner />
      <ApprovalModal />
      <Layout>
        <Routes>
          <Route path="/" element={<LandingView />} />
          <Route path="/login" element={<LeadSignupPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/sign" element={<AuthGuard><SignView /></AuthGuard>} />
          <Route path="/connect" element={<AuthGuard><ConnectView /></AuthGuard>} />
          <Route path="/collect" element={<AuthGuard><CollectView /></AuthGuard>} />
          <Route path="/confirm" element={<AuthGuard><ConfirmView /></AuthGuard>} />
          {/* Business details moved AFTER the connect step — the flow now reads
              the connected platforms and fills the form in for the user. Old
              links (bookmarks, the pre-reorder post-login route) land on the
              screen that replaced it. */}
          <Route path="/project-info" element={<Navigate to="/confirm" replace />} />
          <Route path="/questionnaire" element={<Navigate to="/confirm" replace />} />
          <Route path="/questionnaire/:chatId" element={<Navigate to="/confirm" replace />} />
          <Route path="/shopify/claim" element={<AuthGuard><ShopifyClaimView /></AuthGuard>} />
          <Route path="/processing" element={<AuthGuard><ProcessingView /></AuthGuard>} />
          <Route path="/processing/:chatId" element={<AuthGuard><ProcessingView /></AuthGuard>} />
          <Route path="/generate-report/:chatId" element={<AuthGuard><GenerateReportRedirect /></AuthGuard>} />
          <Route path="/report" element={<AuthGuard><ReportView /></AuthGuard>} />
          <Route path="/report/:chatId" element={<AuthGuard><ReportView /></AuthGuard>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    </BrowserRouter>
  );
}

export default App;
